import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { adminApi } from '@/lib/api';
import { Loader2, Save } from 'lucide-react';
import MediaUploader from '@/components/admin/MediaUploader';
import RichTextEditor from '@/components/admin/RichTextEditor';

const TEXT_FIELDS = [
    { key: 'trust_name', label: 'Trust name' },
    { key: 'trust_short_name', label: 'Short name' },
    { key: 'tagline', label: 'Tagline' },
    { key: 'website', label: 'Website' },
    { key: 'contact_email', label: 'Contact email' },
    { key: 'contact_phone', label: 'Contact phone' },
    { key: 'hero_title', label: 'Hero title', type: 'textarea' },
    { key: 'hero_subtitle', label: 'Hero subtitle', type: 'textarea' },
    { key: 'hero_video_url', label: 'Hero video URL' },
    { key: 'chairman_name', label: 'Chairman name' },
    { key: 'chairman_title', label: 'Chairman title' },
    { key: 'managing_trustee_name', label: 'Managing trustee name' },
    { key: 'managing_trustee_title', label: 'Managing trustee title' },
    { key: 'upi_id', label: 'UPI ID' },
];

const MEDIA_FIELDS = [
    { key: 'logo_url', label: 'Logo', kind: 'image', prefix: 'branding' },
    { key: 'hero_poster_url', label: 'Hero poster image', kind: 'image', prefix: 'hero' },
    { key: 'chairman_photo', label: 'Chairman photo', kind: 'image', prefix: 'team' },
    { key: 'managing_trustee_photo', label: 'Managing trustee photo', kind: 'image', prefix: 'team' },
    { key: 'upi_qr_url', label: 'UPI QR image', kind: 'image', prefix: 'donate' },
    { key: 'brochure_url', label: 'Trust brochure (PDF)', kind: 'pdf', prefix: 'brochures' },
];

const RICH_FIELDS = [
    { key: 'about_short', label: 'About (short)' },
    { key: 'about_long', label: 'About (long)' },
    { key: 'vision', label: 'Vision' },
    { key: 'mission', label: 'Mission' },
    { key: 'quality_policy', label: 'Quality policy' },
    { key: 'volunteer_program_description', label: 'Volunteer program description' },
];

const BANK_FIELDS = [
    { key: 'account_name', label: 'Account name' },
    { key: 'bank_name', label: 'Bank name' },
    { key: 'account_number', label: 'Account number' },
    { key: 'ifsc', label: 'IFSC code' },
    { key: 'branch', label: 'Branch' },
    { key: 'swift', label: 'SWIFT code (optional)' },
];

export default function AdminSettingsPage() {
    const [settings, setSettings] = useState(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        adminApi.getSiteSettings().then(setSettings).catch(() => {});
    }, []);

    if (!settings) return <Skeleton className="h-96 w-full" />;

    const update = (k, v) => setSettings((s) => ({ ...s, [k]: v }));
    const updateBank = (k, v) =>
        setSettings((s) => ({
            ...s,
            bank_details: { ...(s.bank_details || {}), [k]: v },
        }));

    const save = async () => {
        setSaving(true);
        try {
            await adminApi.updateSiteSettings(settings);
            toast.success('Site settings saved');
        } catch {
            toast.error('Save failed');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div data-testid="admin-settings-page">
            <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div>
                    <h1 className="font-display text-2xl font-semibold">Site settings</h1>
                    <p className="text-sm text-muted-foreground">
                        Manage global site-wide content, branding, media assets and bank details.
                    </p>
                </div>
                <Button
                    onClick={save}
                    disabled={saving}
                    className="bg-[hsl(var(--primary))] text-primary-foreground hover:bg-[hsl(var(--primary))]/90"
                    data-testid="admin-settings-save-button"
                >
                    {saving ? (
                        <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                    ) : (
                        <Save className="h-4 w-4 mr-1.5" />
                    )}
                    Save changes
                </Button>
            </div>

            <Tabs defaultValue="general" className="space-y-4">
                <TabsList className="flex flex-wrap gap-1">
                    <TabsTrigger value="general" data-testid="settings-tab-general">General</TabsTrigger>
                    <TabsTrigger value="content" data-testid="settings-tab-content">About & Vision</TabsTrigger>
                    <TabsTrigger value="media" data-testid="settings-tab-media">Media & Branding</TabsTrigger>
                    <TabsTrigger value="bank" data-testid="settings-tab-bank">Bank & UPI</TabsTrigger>
                </TabsList>

                {/* General */}
                <TabsContent value="general">
                    <Card className="bg-card border-border">
                        <CardContent className="p-6">
                            <div className="grid sm:grid-cols-2 gap-4">
                                {TEXT_FIELDS.map((f) => (
                                    <div
                                        key={f.key}
                                        className={f.type === 'textarea' ? 'sm:col-span-2' : ''}
                                    >
                                        <Label className="mb-1.5 block">{f.label}</Label>
                                        {f.type === 'textarea' ? (
                                            <Textarea
                                                rows={f.rows || 3}
                                                value={settings[f.key] || ''}
                                                onChange={(e) => update(f.key, e.target.value)}
                                                data-testid={`admin-settings-${f.key}`}
                                            />
                                        ) : (
                                            <Input
                                                value={settings[f.key] || ''}
                                                onChange={(e) => update(f.key, e.target.value)}
                                                data-testid={`admin-settings-${f.key}`}
                                            />
                                        )}
                                    </div>
                                ))}
                            </div>
                        </CardContent>
                    </Card>
                </TabsContent>

                {/* About / Vision / Mission / Quality Policy */}
                <TabsContent value="content">
                    <Card className="bg-card border-border">
                        <CardContent className="p-6 space-y-6">
                            {RICH_FIELDS.map((f) => (
                                <div key={f.key}>
                                    <Label className="mb-1.5 block">{f.label}</Label>
                                    <RichTextEditor
                                        value={settings[f.key] || ''}
                                        onChange={(v) => update(f.key, v)}
                                        placeholder={`Write the ${f.label.toLowerCase()}…`}
                                        testId={`admin-settings-${f.key}`}
                                        minHeight={160}
                                    />
                                </div>
                            ))}
                        </CardContent>
                    </Card>
                </TabsContent>

                {/* Media */}
                <TabsContent value="media">
                    <Card className="bg-card border-border">
                        <CardContent className="p-6">
                            <div className="grid sm:grid-cols-2 gap-6">
                                {MEDIA_FIELDS.map((f) => (
                                    <div key={f.key}>
                                        <Label className="mb-1.5 block">{f.label}</Label>
                                        <MediaUploader
                                            value={settings[f.key] || ''}
                                            onChange={(v) => update(f.key, v)}
                                            kind={f.kind}
                                            prefix={f.prefix}
                                            testId={`admin-settings-${f.key}`}
                                        />
                                    </div>
                                ))}
                            </div>
                        </CardContent>
                    </Card>
                </TabsContent>

                {/* Bank */}
                <TabsContent value="bank">
                    <Card className="bg-card border-border">
                        <CardContent className="p-6 space-y-4">
                            <div>
                                <div className="text-sm text-muted-foreground mb-3">
                                    These bank details appear <strong>unmasked</strong> on the public Donate page.
                                    Please double-check before saving.
                                </div>
                                <div className="grid sm:grid-cols-2 gap-4">
                                    {BANK_FIELDS.map((f) => (
                                        <div key={f.key}>
                                            <Label className="mb-1.5 block">{f.label}</Label>
                                            <Input
                                                value={settings.bank_details?.[f.key] || ''}
                                                onChange={(e) => updateBank(f.key, e.target.value)}
                                                data-testid={`admin-settings-bank-${f.key}`}
                                            />
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <div className="border-t border-border pt-4">
                                <Label className="mb-1.5 block">UPI ID</Label>
                                <Input
                                    value={settings.upi_id || ''}
                                    onChange={(e) => update('upi_id', e.target.value)}
                                    data-testid="admin-settings-upi_id"
                                />
                            </div>
                            <div>
                                <Label className="mb-1.5 block">UPI QR image</Label>
                                <MediaUploader
                                    value={settings.upi_qr_url || ''}
                                    onChange={(v) => update('upi_qr_url', v)}
                                    kind="image"
                                    prefix="donate"
                                    testId="admin-settings-upi_qr_url"
                                />
                            </div>
                        </CardContent>
                    </Card>
                </TabsContent>
            </Tabs>
        </div>
    );
}
